import mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import progressRing from './progress-ring';

const COLOR_MAP_VALUES = [
  [131, 186, 226],
  [251, 223, 148],
  [153, 248, 196],
  [163, 155, 248],
  [241, 248, 151],
  [148, 241, 248],
  [248, 156, 148],
  [206, 248, 151],
  [151, 251, 223],
  [153, 248, 163],
  [243, 183, 143],
  [178, 248, 153],
  [211, 218, 236],
];
const DOMAIN_NAMES = {
  env: 'Clean Air, Land & Water',
  taxes: 'Fair Taxes',
  family: 'Children & Families',
  criminal: 'Crime &amp; Criminal Justice',
  rights: 'Democratic Rights',
  jobs: 'Jobs & Workplace',
  corporate: 'Corporate Welfare',
};
const SCORES_ORDER = [
  'jobs',
  'taxes',
  'criminal',
  'env',
  'family',
  'corporate',
  'rights',
];
const DISTRICTS_LAYER_NAME = 'uscb-louisiana-district';
const MASK_LAYER_NAME = 'uscb-louisiana-mask-negative';

class ZoomSlider {
  constructor() {
    this.map = undefined;
    this.range = undefined;
    this.container = document.createElement('div');
    this.container.classList.add('mapboxgl-ctrl', 'mapboxgl-ctrl-zoom-slider');
  }

  onAdd(map) {
    this.map = map;
    this.container.innerHTML = `<input
      class="mapboxgl-ctrl-zoom-range"
      style="writing-mode: bt-lr;-webkit-appearance: slider-vertical;width:29px;"
      type="range"
      min="${this.map.getMinZoom()}"
      max="${this.map.getMaxZoom()}"
      value="${this.map.getZoom()}"
      step="0.1"
      orient="vertical">`;
    this.range = this.container.querySelector('.mapboxgl-ctrl-zoom-range');
    this.range.addEventListener('input', this.onRangeChange);
    this.map.on('zoomend', this.onZoomEnd);
    return this.container;
  }

  onRemove() {
    this.container.parentNode.removeChild(this.container);
    this.map.off('zoomend', this.onZoomEnd);
    this.range = undefined;
    this.map = undefined;
  }

  // eslint-disable-next-line class-methods-use-this
  getDefaultPosition() {
    return 'top-right';
  }

  onRangeChange = () => {
    const newZoom = Number.parseFloat(this.range.value);
    this.map.setZoom(newZoom);
  };

  onZoomEnd = () => {
    this.range.value = this.map.getZoom();
  };
}

class Map {
  constructor(container, dataStorage, options) {
    const bounds = [[-94.05, 28.63], [-88.81, 33.125]];

    this.container = container;
    this.wrapper = null;
    try {
      this.wrapper = container.closest('.c-hero__map-wrapper');
    } catch {
      // do nothing
    }
    if (this.wrapper) {
      const wrapperBB = this.wrapper.getBoundingClientRect();
      this.wrapper.style.width = `${Math.min(
        wrapperBB.width,
        wrapperBB.height,
      )}px`;
      this.wrapper.style.height = `${Math.min(
        wrapperBB.width,
        wrapperBB.height,
      )}px`;
    }

    this.dataStorage = dataStorage;
    this.chamber = this.container.dataset.mapChamber.toLowerCase();
    this.features = [];
    this.uniqueGeoIDs = [];
    this.leaveDebounce = null;
    this.hoveredStateGeoId = null;

    this.options = Object.assign(
      {
        container: this.container.id,
        style: this.container.dataset.map,
        center: [
          (bounds[0][0] + bounds[1][0]) / 2.0,
          (bounds[0][1] + bounds[1][1]) / 2.0,
        ],
        bounds,
        maxBounds: bounds,
        maxZoom: 10,
        maxPitch: 0,
        pitchWithRotate: false,
        dragRotate: false,
        touchPitch: false,
        scrollZoom: false,
        customAttribution: 'Boundary data from U.S. Census Bureau',
      },
      options,
    );

    this.map = new mapboxgl.Map(this.options);
    this.map.setMinZoom(this.map.getZoom());
    this.map.dragRotate.disable();
    this.map.touchZoomRotate.disableRotation();

    this.map.addControl(
      new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        mapboxgl,
        placeholder: 'Address search',
      }),
    );

    this.map.addControl(
      new mapboxgl.NavigationControl({
        showCompass: false,
      }),
    );
    this.map.addControl(new ZoomSlider());

    if (this.wrapper) {
      this.wrapper.style.width = '';
      this.wrapper.style.height = '';
      this.map.setMaxBounds(null);
      this.map.resize();
      setTimeout(() => {
        this.map.setMaxBounds(this.map.getBounds());
      }, 50);
    }

    this.popup = new mapboxgl.Popup({
      closeButton: true,
      closeOnClick: false,
      maxWidth: 350,
      focusAfterOpen: false,
      offset: 20,
    });

    this.map.on('load', this.onLoad);
    this.map.on('render', this.onRender);

    this.map.on('touchend', DISTRICTS_LAYER_NAME, this.onDistrictEnter);
    this.map.on('touchend', MASK_LAYER_NAME, this.onMapLeave);

    this.map.on('mouseenter', DISTRICTS_LAYER_NAME, this.onDistrictEnter);
    this.map.on('mouseleave', DISTRICTS_LAYER_NAME, this.onMapLeave);

    this.map.on('mousemove', DISTRICTS_LAYER_NAME, this.onDistrictEnter);
    this.map.on('click', DISTRICTS_LAYER_NAME, this.onDistrictClick);
  }

  onLoad = () => {
    this.features = this.map.queryRenderedFeatures({
      layers: [DISTRICTS_LAYER_NAME],
    });
    this.uniqueGeoIDs = [
      ...new Set(this.features.map(feat => feat.properties.GEOID)),
    ];
    this.uniqueGeoIDs.sort();

    this.map.setPaintProperty(DISTRICTS_LAYER_NAME, 'fill-opacity', [
      'case',
      ['==', ['get', 'GEOID'], ['feature-state', 'GEOID']],
      0.7,
      0.2,
    ]);

    if (this.container.dataset.mapAutocolor === 'true') {
      this.applyAutocolor(this.uniqueGeoIDs, false);
    }
  };

  onRender = () => {
    try {
      if (
        this.map
          .getStyle()
          .layers.map(l => l.id)
          .indexOf(DISTRICTS_LAYER_NAME) > -1
      ) {
        this.features = this.map.queryRenderedFeatures({
          layers: [DISTRICTS_LAYER_NAME],
        });

        const difference = [
          ...new Set(this.features.map(feat => feat.properties.GEOID)),
        ].filter(x => !this.uniqueGeoIDs.includes(x));

        if (difference.length) {
          // if list of features changed, we may be missing some fills colors
          if (this.container.dataset.mapAutocolor) {
            this.applyAutocolor(difference, true);
          }

          this.uniqueGeoIDs = this.uniqueGeoIDs.concat(difference);
        }
      }
    } catch {
      // do nothing
    }
  };

  onDistrictEnter = ev => {
    clearTimeout(this.leaveDebounce);
    this.map.getCanvas().style.cursor = 'pointer';

    // stop mouse emulation since we're handling touch events directly
    if (ev.type === 'touchend') {
      ev.originalEvent.preventDefault();

      // for touch, the second click should remove popup
      const current = this.popup.isOpen()
        ? this.popup.getElement().querySelector('[data-map-popup-geoid]')
        : null;
      if (
        current &&
        current.dataset.mapPopupGeoid === `${ev.features[0].properties.GEOID}`
      ) {
        this.popup.remove();
        return;
      }

      if (
        this.dataStorage.getRecord(
          this.chamber,
          `${ev.features[0].properties.GEOID}`,
        )
      ) {
        this.popup.addTo(this.map);
      }
    }

    if (ev.features.length > 0) {
      if (this.hoveredStateGeoId !== ev.features[0].properties.GEOID) {
        let newGeoId = ev.features[0].properties.GEOID;
        if (newGeoId === '22ZZZ') {
          newGeoId = null;
        }
        this.features
          .filter(
            feature => feature.properties.GEOID === this.hoveredStateGeoId,
          )
          .forEach(feature => {
            this.map.setFeatureState(feature, { GEOID: false });
          });

        this.hoveredStateGeoId = newGeoId;

        this.features
          .filter(
            feature => feature.properties.GEOID === this.hoveredStateGeoId,
          )
          .forEach(feature => {
            this.map.setFeatureState(feature, {
              GEOID: feature.properties.GEOID,
            });
          });

        this.showPopup(ev);
      } else {
        this.popup.setLngLat(ev.lngLat);
        if (this.popup.getElement()) {
          this.popup
            .getElement()
            .classList.toggle('is-touch', ev.type.startsWith('touch'));
        }
      }
    }
  };

  onMapLeave = () => {
    this.leaveDebounce = setTimeout(() => {
      this.map.getCanvas().style.cursor = '';
      if (this.hoveredStateGeoId) {
        this.features
          .filter(
            feature => feature.properties.GEOID === this.hoveredStateGeoId,
          )
          .forEach(feature => {
            this.map.setFeatureState(feature, { GEOID: false });
          });
        this.hoveredStateGeoId = null;
      }
      this.popup.remove();
    }, 50);
  };

  onDistrictClick = ev => {
    const record = this.dataStorage.getRecord(
      this.chamber,
      `${ev.features[0].properties.GEOID}`,
    );
    if (!record || !record.link) {
      return;
    }
    window.open(record.link, '_blank');
  };

  createPopup(geoid) {
    const record = this.dataStorage.getRecord(this.chamber, geoid);
    if (!record) {
      return '';
    }
    return /* html */ `<div class="c-scorecard" data-map-popup-geoid="${geoid}">
      <div class="c-scorecard__header">
        <div class="c-scorecard__rep">
          <h5 class="c-scorecard__person">
            <span class="c-scorecard__title">${record.chamber}.</span>
            <span class="c-scorecard__name">${record.name}</span>
          </h5>
          <p class="c-scorecard__district">${record.district
            .replace('SD', 'Senate District')
            .replace('HD', 'House District')
            .replace('-', ' - ')}</p>
        </div>
        <div class="c-scorecard__overall">
          ${
            Number.isNaN(record.score_overall)
              ? ''
              : /* html */ `<span class="c-scorecard__score c-scorecard__score--grade-${record.grade_overall.toLowerCase()}">${Math.round(
                  record.score_overall * 100,
                )}%</span>`
          }
          ${this.createProgress(
            record.score_overall,
            record.grade_overall,
            23,
            'tiny',
          )}
          <div class="c-scorecard__footer">
            <a class="c-scorecard__profile-link" href="${
              record.link
            }">View Details</a>
          </div>
        </div>
        <div class="c-scorecard__desc">Click a district for details</div>
      </div>
      <div class="c-scorecard__content">
        <table class="c-scorecard__grades">
            <tbody>
              ${SCORES_ORDER.map(this.createRow.bind(this, record)).join('')}
            </tbody>
        </table>
      </div>
    <div>`;
  }

  createRow(record, domain) {
    return /* html */ `<tr>
      <td><img src="/wp-content/themes/for-the-people-chisel/dist/assets/images/black-icons/${domain}.png" /></td>
      <th scope="row">${DOMAIN_NAMES[domain]}</th>
      <td>${this.createProgress(
        record[`score_${domain}`],
        record[`grade_${domain}`],
      )}</td>
    </tr>`;
  }

  // eslint-disable-next-line class-methods-use-this
  createProgress(maybeScore, grade = 'N/A', radius = 16, variant = 'nano') {
    const score = !maybeScore || Number.isNaN(maybeScore) ? 0 : maybeScore;
    const circumference = 0.86 * Math.PI;
    const strokeDashoffset = circumference - score * circumference;
    return /* html */ `<div class="c-progress c-progress--${variant} c-progress--grade-${grade.toLowerCase()}">
      <div class="c-progress__bg">
        <div>
          <p class="grade">${grade}</p>
        </div>
      </div>
      <svg
        class="c-progress__ring"
        width="${2 * radius}"
        height="${2 * radius}"
        viewBox="0 0 1 1">
        <circle
          class="c-progress__ring-border"
          stroke="rgba(50,50,50,0.05)"
          stroke-width="0.14"
          stroke-linecap="round"
          fill="transparent"
          r="0.43"
          cx="0.5"
          cy="0.5"/>
        <circle
          class="c-progress__ring-track"
          stroke="currentColor"
          stroke-opacity="0.5"
          stroke-width="0.08"
          stroke-linecap="round"
          fill="transparent"
          r="0.43"
          cx="0.5"
          cy="0.5"/>
        <circle
          class="c-progress__ring-circle"
          stroke="currentColor"
          stroke-width="0.08"
          stroke-linecap="round"
          fill="transparent"
          r="0.43"
          cx="0.5"
          cy="0.5"
          data-dashoffset="${strokeDashoffset}"
          style="stroke-dasharray: ${circumference}, ${circumference}; stroke-dashoffset: ${circumference}"/>
      </svg>
    </div>`;
  }

  showPopup = ev => {
    const contents = this.createPopup(`${ev.features[0].properties.GEOID}`);
    if (contents) {
      this.popup
        .setLngLat(ev.lngLat)
        .setHTML(contents)
        .addTo(this.map);

      this.popup
        .getElement()
        .classList.toggle('is-touch', ev.type.startsWith('touch'));

      progressRing(this.popup.getElement());
    } else {
      this.popup.remove();
    }
  };

  applyAutocolor(uniqueGeoIDs, isUpdating = false) {
    let offset = 0;
    let fills;

    if (isUpdating) {
      fills = this.map.getPaintProperty(
        DISTRICTS_LAYER_NAME,
        'fill-color',
        fills,
      );
      fills.pop();
      offset = this.uniqueGeoIDs.length;
    } else {
      fills = ['match', ['to-string', ['get', 'GEOID']]];
    }

    uniqueGeoIDs.forEach((geoid, idx) => {
      fills.push(`${geoid}`);
      const [r, g, b] = COLOR_MAP_VALUES[
        (offset + idx) % COLOR_MAP_VALUES.length
      ];
      fills.push(['rgb', r, g, b]);
    });

    fills.push(['rgb', 0, 0, 0]);
    this.map.setPaintProperty(DISTRICTS_LAYER_NAME, 'fill-color', fills);
  }
}

class MapDataStorage {
  constructor(rawData) {
    this.availableData = { rep: {}, sen: {} };

    const startIndex = rawData.analysis.findIndex(row => !!row[0]) + 1;
    const headers = [
      'people_id',
      'name',
      'party',
      'chamber',
      'district',
      'score_jobs',
      'score_taxes',
      'score_criminal',
      'score_env',
      'score_family',
      'score_corporate',
      'score_rights',
      'score_overall',
      false,
      'grade_jobs',
      'grade_taxes',
      'grade_criminal',
      'grade_env',
      'grade_family',
      'grade_corporate',
      'grade_rights',
      'grade_overall',
    ];
    for (let i = startIndex; i < rawData.analysis.length; i += 1) {
      const record = {};
      for (let j = 0; j < headers.length; j += 1) {
        if (headers[j] && rawData.analysis[i][0]) {
          record[headers[j]] = headers[j].startsWith('score_')
            ? Number.parseFloat(rawData.analysis[i][j])
            : rawData.analysis[i][j];
        }
      }
      if (record.chamber && record.district) {
        record.link = `/politician?id=${
          record.people_id
        }-${record.name.toLowerCase().replace(' ', '-')}`;

        this.availableData[record.chamber.toLowerCase()][
          `22${record.district.substr(3)}`
        ] = record;
      }
    }
  }

  getRecord = (chamber, geoid) => this.availableData[chamber][geoid];
}

export default function() {
  mapboxgl.accessToken =
    'pk.eyJ1IjoidG9nZXRoZXJsYSIsImEiOiJjazM4cXhqenIwYXdzM2hucjFqZXFkbnVwIn0.TqBwfsPoOIUu_7hz9NzC9g';

  if (
    !window.mapJsonData ||
    (!window.mapJsonData.analysis ||
      !Array.isArray(window.mapJsonData.analysis))
  ) {
    window.mapJsonData = [];
    console.warn('Empty map JSON data.');
  }

  const dataStorage = new MapDataStorage(window.mapJsonData);

  Array.from(document.querySelectorAll('[data-map]')).map(
    container => new Map(container, dataStorage),
  );
}



// WEBPACK FOOTER //
// ./src/scripts/modules/map.js